import React, { useState, useEffect } from 'react';
import gisService from '@/modules/utility/gis/services/gisService';
import { PageSkeleton } from '@/components/base/Skeleton';

interface DataLayer {
  id: number;
  layer_code: string;
  layer_name: string;
  layer_name_local: string;
  description: string;
  data_source_type: string;
  table_name: string | null;
  api_endpoint: string | null;
  geometry_column: string;
  geometry_type: string;
  symbol_type: string;
  symbol_field: string | null;
  symbol_config: any;
  popup_template: string | null;
  popup_fields: any;
  filter_sql: string | null;
  filter_params: any;
  auto_refresh: boolean;
  refresh_interval: number | null;
  is_editable: boolean;
  is_deletable: boolean;
  edit_permission: string | null;
  required_permission: string | null;
  min_zoom: number;
  max_zoom: number;
  is_visible_default: boolean;
  is_clustered: boolean;
  cluster_distance: number;
  icon: string;
  display_order: number;
  sequence_id: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
}

const DataLayers: React.FC = () => {
  // State
  const [layers, setLayers] = useState<DataLayer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dataSourceFilter, setDataSourceFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<keyof DataLayer>('sequence_id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingLayer, setEditingLayer] = useState<DataLayer | null>(null);
  const [deletingLayer, setDeletingLayer] = useState<DataLayer | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    layer_code: '',
    layer_name: '',
    layer_name_local: '',
    description: '',
    data_source_type: 'database',
    table_name: '',
    api_endpoint: '',
    geometry_column: 'geom',
    geometry_type: 'Point',
    symbol_type: 'marker',
    symbol_field: '',
    symbol_config: null,
    popup_template: '',
    popup_fields: null,
    filter_sql: '',
    filter_params: null,
    auto_refresh: false,
    refresh_interval: null,
    is_editable: false,
    is_deletable: false,
    edit_permission: '',
    required_permission: '',
    min_zoom: 0,
    max_zoom: 22,
    is_visible_default: true,
    is_clustered: false,
    cluster_distance: 50,
    icon: 'ri-map-pin-line',
    display_order: 0,
    sequence_id: 0,
    is_active: true
  });

  const itemsPerPage = 10;

  // Fetch data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const response = await gisService.getAllDataLayers();

      if (response.status === 200 && response.data) {
        setLayers(response.data);
      }
    } catch (error) {
      console.error('Error fetching data layers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Data source type counts
  const databaseCount = layers.filter(l => l.data_source_type === 'database').length;
  const apiCount = layers.filter(l => l.data_source_type === 'api').length;

  // Filter and sort
  const filteredLayers = layers.filter(layer => {
    const matchesSearch = layer.layer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         layer.layer_code.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDataSource = dataSourceFilter === 'all' || layer.data_source_type === dataSourceFilter;

    return matchesSearch && matchesDataSource;
  });

  const sortedLayers = [...filteredLayers].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];

    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortOrder === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return sortOrder === 'asc'
      ? (aValue > bValue ? 1 : -1)
      : (aValue < bValue ? 1 : -1);
  });

  // Pagination
  const totalPages = Math.ceil(sortedLayers.length / itemsPerPage);
  const paginatedLayers = sortedLayers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleSort = (column: keyof DataLayer) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const handleCreate = () => {
    setEditingLayer(null);
    setFormData({
      layer_code: '',
      layer_name: '',
      layer_name_local: '',
      description: '',
      data_source_type: 'database',
      table_name: '',
      api_endpoint: '',
      geometry_column: 'geom',
      geometry_type: 'Point',
      symbol_type: 'marker',
      symbol_field: '',
      symbol_config: null,
      popup_template: '',
      popup_fields: null,
      filter_sql: '',
      filter_params: null,
      auto_refresh: false,
      refresh_interval: null,
      is_editable: false,
      is_deletable: false,
      edit_permission: '',
      required_permission: '',
      min_zoom: 0,
      max_zoom: 22,
      is_visible_default: true,
      is_clustered: false,
      cluster_distance: 50,
      icon: 'ri-map-pin-line',
      display_order: 0,
      sequence_id: 0,
      is_active: true
    });
    setShowCreateModal(true);
  };

  const handleEdit = (layer: DataLayer) => {
    setEditingLayer(layer);
    setFormData({
      layer_code: layer.layer_code,
      layer_name: layer.layer_name,
      layer_name_local: layer.layer_name_local || '',
      description: layer.description || '',
      data_source_type: layer.data_source_type,
      table_name: layer.table_name || '',
      api_endpoint: layer.api_endpoint || '',
      geometry_column: layer.geometry_column,
      geometry_type: layer.geometry_type,
      symbol_type: layer.symbol_type,
      symbol_field: layer.symbol_field || '',
      symbol_config: layer.symbol_config,
      popup_template: layer.popup_template || '',
      popup_fields: layer.popup_fields,
      filter_sql: layer.filter_sql || '',
      filter_params: layer.filter_params,
      auto_refresh: layer.auto_refresh,
      refresh_interval: layer.refresh_interval,
      is_editable: layer.is_editable,
      is_deletable: layer.is_deletable,
      edit_permission: layer.edit_permission || '',
      required_permission: layer.required_permission || '',
      min_zoom: layer.min_zoom,
      max_zoom: layer.max_zoom,
      is_visible_default: layer.is_visible_default,
      is_clustered: layer.is_clustered,
      cluster_distance: layer.cluster_distance,
      icon: layer.icon,
      display_order: layer.display_order,
      sequence_id: layer.sequence_id,
      is_active: layer.is_active
    });
    setShowCreateModal(true);
  };

  const handleDelete = (layer: DataLayer) => {
    setDeletingLayer(layer);
    setShowDeleteModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (editingLayer) {
        await gisService.updateDataLayer(editingLayer.id, formData);
      } else {
        await gisService.createDataLayer(formData);
      }

      setShowCreateModal(false);
      await fetchData();
    } catch (error) {
      console.error('Error saving data layer:', error);
      alert('Failed to save data layer');
    }
  };

  const confirmDelete = async () => {
    if (!deletingLayer) return;

    try {
      await gisService.deleteDataLayer(deletingLayer.id);
      setShowDeleteModal(false);
      setDeletingLayer(null);
      await fetchData();
    } catch (error) {
      console.error('Error deleting data layer:', error);
      alert('Failed to delete data layer');
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else if (type === 'number') {
      setFormData(prev => ({ ...prev, [name]: value === '' ? null : Number(value) }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  if (isLoading) {
    return (
      <PageSkeleton />
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Data Layers Management</h1>
        <p className="mt-2 text-gray-600">
          Manage data layers from database tables and API endpoints
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Layers</p>
              <p className="text-2xl font-bold text-gray-900">{layers.length}</p>
            </div>
            <i className="ri-database-2-line text-3xl text-blue-600"></i>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Database Layers</p>
              <p className="text-2xl font-bold text-gray-900">{databaseCount}</p>
            </div>
            <i className="ri-table-line text-3xl text-green-600"></i>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">API Layers</p>
              <p className="text-2xl font-bold text-gray-900">{apiCount}</p>
            </div>
            <i className="ri-code-line text-3xl text-purple-600"></i>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by name or code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          {/* Data Source Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => setDataSourceFilter('all')}
              className={`px-4 py-2 rounded-lg ${
                dataSourceFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All ({layers.length})
            </button>
            <button
              onClick={() => setDataSourceFilter('database')}
              className={`px-4 py-2 rounded-lg ${
                dataSourceFilter === 'database'
                  ? 'bg-green-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Database ({databaseCount})
            </button>
            <button
              onClick={() => setDataSourceFilter('api')}
              className={`px-4 py-2 rounded-lg ${
                dataSourceFilter === 'api'
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              API ({apiCount})
            </button>
          </div>

          {/* Create Button */}
          <button
            onClick={handleCreate}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <i className="ri-add-line"></i>
            Create Layer
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  onClick={() => handleSort('layer_code')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Layer Code {sortBy === 'layer_code' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleSort('layer_name')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Layer Name {sortBy === 'layer_name' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleSort('data_source_type')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Source Type {sortBy === 'data_source_type' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th
                  onClick={() => handleSort('geometry_type')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                >
                  Geometry {sortBy === 'geometry_type' && (sortOrder === 'asc' ? '↑' : '↓')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedLayers.map((layer) => (
                <tr key={layer.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <i className={`${layer.icon} text-xl mr-2 text-gray-600`}></i>
                      <span className="text-sm font-medium text-gray-900">{layer.layer_code}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">{layer.layer_name}</div>
                    {layer.layer_name_local && (
                      <div className="text-sm text-gray-500">{layer.layer_name_local}</div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      layer.data_source_type === 'database'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {layer.data_source_type}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {layer.geometry_type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      layer.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {layer.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button
                      onClick={() => handleEdit(layer)}
                      className="text-blue-600 hover:text-blue-900 mr-3"
                    >
                      <i className="ri-edit-line"></i> Edit
                    </button>
                    <button
                      onClick={() => handleDelete(layer)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <i className="ri-delete-bin-line"></i> Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * itemsPerPage + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * itemsPerPage, sortedLayers.length)}
                  </span>{' '}
                  of <span className="font-medium">{sortedLayers.length}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                        currentPage === page
                          ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      }`}
                    >
                      {page}
                    </button>
                  ))}
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4">
              <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-900">
                  {editingLayer ? 'Edit Data Layer' : 'Create Data Layer'}
                </h2>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              {/* Basic Information */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Basic Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Layer Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="layer_code"
                      value={formData.layer_code}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Layer Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="layer_name"
                      value={formData.layer_name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Layer Name (Local)
                    </label>
                    <input
                      type="text"
                      name="layer_name_local"
                      value={formData.layer_name_local}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Icon
                    </label>
                    <input
                      type="text"
                      name="icon"
                      value={formData.icon}
                      onChange={handleInputChange}
                      placeholder="ri-map-pin-line"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Data Source Configuration */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Data Source Configuration</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Data Source Type <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="data_source_type"
                      value={formData.data_source_type}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="database">Database Table</option>
                      <option value="api">API Endpoint</option>
                    </select>
                  </div>

                  {formData.data_source_type === 'database' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Table Name
                      </label>
                      <input
                        type="text"
                        name="table_name"
                        value={formData.table_name}
                        onChange={handleInputChange}
                        placeholder="e.g., public.election_booths"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  )}

                  {formData.data_source_type === 'api' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        API Endpoint
                      </label>
                      <input
                        type="text"
                        name="api_endpoint"
                        value={formData.api_endpoint}
                        onChange={handleInputChange}
                        placeholder="e.g., /api/election/booths"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Geometry Configuration */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Geometry Configuration</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Geometry Column
                    </label>
                    <input
                      type="text"
                      name="geometry_column"
                      value={formData.geometry_column}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Geometry Type
                    </label>
                    <select
                      name="geometry_type"
                      value={formData.geometry_type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="Point">Point</option>
                      <option value="LineString">LineString</option>
                      <option value="Polygon">Polygon</option>
                      <option value="MultiPoint">MultiPoint</option>
                      <option value="MultiLineString">MultiLineString</option>
                      <option value="MultiPolygon">MultiPolygon</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Symbol Type
                    </label>
                    <select
                      name="symbol_type"
                      value={formData.symbol_type}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="marker">Marker</option>
                      <option value="circle">Circle</option>
                      <option value="line">Line</option>
                      <option value="fill">Fill</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Display Settings */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Display Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Min Zoom
                    </label>
                    <input
                      type="number"
                      name="min_zoom"
                      value={formData.min_zoom}
                      onChange={handleInputChange}
                      min="0"
                      max="22"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Max Zoom
                    </label>
                    <input
                      type="number"
                      name="max_zoom"
                      value={formData.max_zoom}
                      onChange={handleInputChange}
                      min="0"
                      max="22"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Display Order
                    </label>
                    <input
                      type="number"
                      name="display_order"
                      value={formData.display_order}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Sequence ID
                    </label>
                    <input
                      type="number"
                      name="sequence_id"
                      value={formData.sequence_id}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Clustering Settings */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Clustering Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_clustered"
                      checked={formData.is_clustered}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Enable Clustering
                    </label>
                  </div>

                  {formData.is_clustered && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Cluster Distance
                      </label>
                      <input
                        type="number"
                        name="cluster_distance"
                        value={formData.cluster_distance}
                        onChange={handleInputChange}
                        min="10"
                        max="200"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Additional Settings */}
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Additional Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_visible_default"
                      checked={formData.is_visible_default}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Visible by Default
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_editable"
                      checked={formData.is_editable}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Editable
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_deletable"
                      checked={formData.is_deletable}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Deletable
                    </label>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="auto_refresh"
                      checked={formData.auto_refresh}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Auto Refresh
                    </label>
                  </div>

                  {formData.auto_refresh && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Refresh Interval (seconds)
                      </label>
                      <input
                        type="number"
                        name="refresh_interval"
                        value={formData.refresh_interval || ''}
                        onChange={handleInputChange}
                        min="5"
                        placeholder="e.g., 30"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  )}

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      name="is_active"
                      checked={formData.is_active}
                      onChange={handleInputChange}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label className="ml-2 block text-sm text-gray-900">
                      Active
                    </label>
                  </div>
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex justify-end gap-4 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingLayer ? 'Update Layer' : 'Create Layer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingLayer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
              <i className="ri-delete-bin-line text-2xl text-red-600"></i>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
              Delete Data Layer
            </h3>
            <p className="text-gray-600 text-center mb-6">
              Are you sure you want to delete <strong>{deletingLayer.layer_name}</strong>?
              This action cannot be undone.
            </p>
            <div className="flex gap-4">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataLayers;
